import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  EmailValidator,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { CirfClientInfo } from "../cirf-client-info";
import { DatePipe } from "@angular/common";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { CirfService } from "../cirf.service";
import { Utils } from "../../common/utils";
import { dropDowns } from "../../shared/drop-downs";
import { CirfDocument } from "../cirf-document";
import { Cirf } from "../cirf";
import { ConfirmDialogService } from "../../common/confirm-dialog/confirm-dialog.service";
import { FileUploadService } from "../../common/file-upload/file-upload.service";
import {
  CIRF_NAME_KEYS,
  CREDIT_CARD,
  DpBooleanValueTypes,
  IdentificationOptions,
  PASSPORT_CANADIAN_ID,
  UploadedFileDescription,
} from "../../shared/constants";
import { ConnectDialog } from "../../common/confirm-dialog/connect-dialog";
import { map, startWith } from "rxjs/operators";
import { CustomPicklist } from "../../common/custom-picklist";
import { DriverLicenseInfo } from "../../common/driver-license-info";
import { Identification } from "../../shared/identification";
import { MatSelect } from "@angular/material/select";
import {
  MatDatepicker,
  MatDatepickerInputEvent,
} from "@angular/material/datepicker";
import { AddressFormComponent } from "../../common/address-form/address-form.component";
import { CommonService } from "../../common/common.service";
import { AutoSuggest } from "../../common/auto-suggest/dp-auto-suggest";
import moment from "moment";
import { CirfConfigService } from "../config/cirf-config-service";
import { CirfUdf } from "../cirf-udf";
import { CIRF_CONFIG_KEYS } from "../config/cirf-config-keys";
import { DpConnectAutoSuggestComponent } from "../../common/auto-suggest/dp-connect-auto-suggest.component";
import { Subscription } from "rxjs";

@Component({
  selector: "dp-connect-cirf",
  templateUrl: "./connect-cirf.component.html",
})
export class ConnectCirfComponent implements OnInit, OnDestroy {
  _clientId: string;
  @Input() set clientId(value: string) {
    if (this._clientId != value) {
      this._clientId = value;
      // We need the timeout here in order for the "cirfFormGroup.valueChanges.pipe" to work
      // TOTO Investigate a way to solve the above without a time out
      setTimeout(() => {
        this.matterTypeText = this.cirfService.matterTypeText;
        this.setUpClient(Number(this.clientId));
        this.startPolling();
        this.today = this.datePipe.transform(
          Date.now(),
          "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
        );
      }, 100);
    }
  }

  get clientId(): string {
    return this._clientId;
  }

  @Input() sectionKey: string;

  @ViewChild("genderSelect") genderSelect: MatSelect;
  @ViewChild("birthDatePicker") birthDatePicker: MatDatepicker<Date>;
  @ViewChild("primaryExpiryDatePicker")
  primaryExpiryDatePicker: MatDatepicker<Date>;
  @ViewChild("businessAddress") businessAddress: AddressFormComponent;
  @ViewChild("spouseAutoSuggest")
  spouseAutoSuggest: DpConnectAutoSuggestComponent;

  genderOptions: any = dropDowns.genderOptions;
  yesNoOptions: any = dropDowns.yesNoOptions;
  residentStatusOptions: any = dropDowns.residentStatusOptions;
  spousalStatusOptions: any = dropDowns.spousalStatusOptions;
  yesNoOptionsMandatory: any = dropDowns.yesNoOptionsMandatory;
  firstTimeHomeBuyerOptions: any = dropDowns.firstTimeHomeBuyerOptions;
  identificationOptions: any[];
  today: any = new Date();
  _cirfClientInfo: CirfClientInfo;
  matterTypeText: string;
  cirfDocuments: CirfDocument[] = [];
  scheduler: any;
  filteredPrimaryIdentificationOptions: any[];
  filteredSecondaryIdentificationOptions: any[];
  //loadingDocumentsComplete flag to indicate getting documents from the backend is completed
  loadingDocumentsComplete: boolean = false;
  uploadDriverLicenseOptions = dropDowns.yesNoOptionsMandatory;
  frontText: string = "Front";
  backText: string = "Back";
  isSpouseNameReadonly: boolean = false;
  oldSpousalStatus: string;
  spousalStatusUpdateSubscription: Subscription;
  oldSpouseOnTitle: string;
  spouseOnTitleSubscription: Subscription;
  spouseDropDownAutoSuggest: AutoSuggest[] = [];

  constructor(
    private datePipe: DatePipe,
    public router: Router,
    private route: ActivatedRoute,
    public cirfService: CirfService,
    public fileUploadService: FileUploadService,
    public confirmDialogService: ConfirmDialogService,
    public commonService: CommonService,
    private cirfConfigService: CirfConfigService
  ) {}

  ngOnInit() {}

  loadSpouseDropDownOptions() {
    this.spouseDropDownAutoSuggest = [];
    this.cirfClientsFormArray.value
      .filter((client) => client.id != this.clientId)
      .forEach((clientForm) => {
        const client = new CirfClientInfo(clientForm);
        this.spouseDropDownAutoSuggest.push(
          new AutoSuggest(client.id, client.fullName, null, null, client)
        );
      });
  }

  setUpIdentificationOptions() {
    this.cirfService
      .getCustomePickList(this.cirf.cirfGuid, "IDENTIFICATION_DOCUMENT_TYPE")
      .subscribe((res: CustomPicklist[]) => {
        this.identificationOptions = [];
        res.forEach((item) => {
          this.identificationOptions.push({
            name: item.customPickListItemValue,
            value: item.customPickListItemValue,
          });
        });
        this.primaryIdentificationFormGroup.controls.identificationTypeCode.valueChanges
          .pipe(
            startWith(""),
            map((value) => this._filter(value, this.identificationOptions))
          )
          .subscribe((res) => {
            this.filteredPrimaryIdentificationOptions = res;
          });

        this.secondaryIdentificationFormGroup.controls.identificationTypeCode.valueChanges
          .pipe(
            startWith(""),
            map((value) => this._filter(value, this.identificationOptions))
          )
          .subscribe((res) => {
            this.filteredSecondaryIdentificationOptions = res;
          });
      });
  }

  private _filter(value, options): any[] {
    let filterValue = value.toLowerCase();
    return options.filter((option) =>
      option.value.toLowerCase().includes(filterValue)
    );
  }

  setUpClient(clientId: number): void {
    if (!!clientId) {
      this._cirfClientInfo = this.cirfService.clients.find(
        (item) => item.id == clientId.toString()
      );
      if (!this._cirfClientInfo) {
        this._cirfClientInfo = new CirfClientInfo();
        this._cirfClientInfo.id = Utils.uuid();
        this._cirfClientInfo.gender = "QUESTION";
        this.cirfService.clients.push(this._cirfClientInfo);
        this.cirfService.setUpFormControl(this.cirf);
      }
    }
    if (!this._cirfClientInfo.agreedToUploadDL) {
      this._cirfClientInfo.agreedToUploadDL = "";
    }
    this.clientId = this._cirfClientInfo.id;
    if (!this._cirfClientInfo.showAllFields) {
      this._cirfClientInfo.showAllFields = this.isUploadDriverLicenseNo();
    }

    if (clientId > 0) {
      Utils.invokeFieldLevelValidation(this.formGroup);
    }
    if (this._cirfClientInfo.isClientInfoFromDL) {
      this.disableFormControls();
    }
    this.isSpouseNameReadonly = !!this._cirfClientInfo.spouseName;
    this.setupClientNotes();
    this.setUpIdentificationOptions();
    this.onChanges();
    this.setSpousalStatusSubscription();
    this.setSpouseOnTitleSubscription();
    this.loadSpouseDropDownOptions();
  }

  setSpousalStatusSubscription() {
    this.oldSpousalStatus = this.formGroup.value["spousalStatus"];
    this.spousalStatusUpdateSubscription = this.formGroup
      .get("spousalStatus")
      .valueChanges.subscribe((value) => {
        // spousalStatus gets validated every 5 seconds see utils#updateFormGroupRequiredAttributesForClient
        if (value !== this.formGroup.value["spousalStatus"]) {
          this.oldSpousalStatus = this.formGroup.value["spousalStatus"];
          console.log("this.oldSpousalStatus = " + this.oldSpousalStatus);
        }
      });
  }

  setSpouseOnTitleSubscription() {
    this.oldSpousalStatus = this.formGroup.value["isSpouseOnTitle"];
    this.spouseOnTitleSubscription = this.formGroup
      .get("isSpouseOnTitle")
      .valueChanges.subscribe((value) => {
        // isSpouseOnTitle gets validated every 5 seconds see utils#updateFormGroupRequiredAttributesForClient
        if (value !== this.formGroup.value["isSpouseOnTitle"]) {
          this.oldSpouseOnTitle = this.formGroup.value["isSpouseOnTitle"];
          console.log("this.oldSpouseOnTitle = " + this.oldSpouseOnTitle);
        }
      });
  }

  setupClientNotes(): void {
    if (this.formGroup) {
      if (!this.formGroup["clientNotes"]) {
        this.formGroup.addControl("clientNotes", new FormControl());
      }
      this.formGroup?.controls?.clientNotes?.setValue(
        this.cirf?.cirfMatterData?.clientNotes
      );
    }
  }

  saveClientNotes(): void {
    if (this.formGroup?.controls?.clientNotes?.value) {
      this.cirfService.cirfFormGroup
        .get("cirfMatterData")
        ["controls"].clientNotes.setValue(
          this.formGroup.controls.clientNotes.value
        );
    }
  }

  onChanges(): void {
    this.updateValidation();
    this.cirfService.saveChangesOnCirfFormChanges();
  }

  get formGroup(): FormGroup {
    return this.cirfService.cirfFormGroup
      ?.get("cirfMatterData")
      ["controls"].cirfClients.controls.find(
        (item) => !!this.clientId && item.value.id == this.clientId
      );
  }

  getCirfClientFormGroupById(id: string): FormGroup {
    return this.cirfService.cirfFormGroup
      .get("cirfMatterData")
      ["controls"].cirfClients.controls.find(
        (item) => !!id && item.value.id == id
      );
  }

  get cirfClientsFormArray(): FormArray {
    return this.cirfService.cirfFormGroup.get("cirfMatterData")["controls"]
      .cirfClients;
  }

  removeClientWithName(name: string) {
    const index = this.cirfClientsFormArray.value.findIndex((clientForm) => {
      const cirfClient = new CirfClientInfo(clientForm);
      return cirfClient.fullName === name;
    });
    if (index > -1) {
      this.cirfService.cirfFormGroup
        .get("cirfMatterData")
        ["controls"].cirfClients.removeAt(index);
    }
  }

  // Custom Validations

  updatePhoneValidation(): void {
    Utils.updatePhoneValidationForClient(
      this.formGroup,
      !this.allowMissingFields
    );
  }

  async saveClientForm(route?: string[]) {
    this.saveClientNotes();
    if (!!this.cirfService.cirf) {
      let cirf = await this.cirfService.updateCirf("INPROGRESS");
      if (cirf) {
        if (route) {
          this.router.navigate(route, { relativeTo: this.route });
        }
      }
    } else if (route) {
      this.router.navigate(route, { relativeTo: this.route });
    }
  }

  backToClientListPage(): void {
    this.saveClientForm(["../../../cirfs"]);
  }

  isCanadianResident(): boolean {
    return (
      this.formGroup &&
      this.formGroup.getRawValue().isCanadianResident &&
      this.formGroup.getRawValue().isCanadianResident == DpBooleanValueTypes.YES
    );
  }

  isPermanentResident(): boolean {
    return (
      this.formGroup &&
      this.formGroup.getRawValue().residentStatus &&
      this.formGroup.getRawValue().residentStatus == "PERMANENT_RESIDENT"
    );
  }

  isCanadianResidentChanged(event): void {
    if (this.isCanadianResident()) {
      Utils.setFieldAsRequired(this.formGroup.controls.residentStatus);
    } else {
      Utils.resetField(this.formGroup.controls.residentStatus, "");
    }
    this.updateValidation();
  }

  isSpousalStatusVisible(): boolean {
    return (
      (this.formGroup &&
        this.formGroup.getRawValue().gender &&
        this.formGroup.getRawValue().gender === "") ||
      this.formGroup.getRawValue().gender === "MALE" ||
      this.formGroup.getRawValue().gender === "FEMALE"
    );
  }

  isSpousalStatusMarried(): boolean {
    return (
      this.formGroup &&
      this.formGroup.getRawValue().spousalStatus &&
      this.formGroup.getRawValue().spousalStatus == "MARRIED"
    );
  }

  getSpousalStatus(): string {
    return this.formGroup && this.formGroup.getRawValue().spousalStatus;
  }

  getFamilyResidence(): string {
    return this.formGroup && this.formGroup.getRawValue().isFamilyResidence;
  }

  getSpouseOnTitle(): string {
    return this.formGroup && this.formGroup.getRawValue().isSpouseOnTitle;
  }

  async isSpousalStatusChanged(event): Promise<void> {
    if (this.getSpousalStatus() == "MARRIED") {
      Utils.setFieldAsRequired(this.formGroup.controls.isFamilyResidence);
      Utils.setFieldAsRequired(this.formGroup.controls.isSpouseOnTitle);
    } else {
      let canContinue = true;
      if (this.oldSpousalStatus === "MARRIED" && this.isSpouseNameReadonly) {
        canContinue = await this.confirmSpousalStatusChange();
      }
      if (canContinue) {
        this.removeSpousalLink(this.formGroup.getRawValue().spouseId);
        Utils.resetField(this.formGroup.controls.isFamilyResidence, "");
        Utils.resetField(this.formGroup.controls.isSpouseOnTitle, "");
        Utils.resetField(this.formGroup.controls.spouseName, "");
        Utils.resetField(this.formGroup.controls.spouseId, "");
        this.isSpouseNameReadonly = false;
      } else {
        Utils.resetField(
          this.formGroup.controls.spousalStatus,
          this.oldSpousalStatus
        );
      }
    }
    this.updateValidation();
  }

  async confirmSpousalStatusChange(): Promise<boolean> {
    const connectDialog = new ConnectDialog();
    const spouseName = this.formGroup.getRawValue().spouseName;
    connectDialog.okBtnText = "Yes";
    connectDialog.cancelBtnText = "No";
    connectDialog.message =
      "Changing the spousal status from “Married” to a different status will result in the reset of all spousal details that were previously entered.<br>" +
      `Please note that ${spouseName} will still remain as a contact on file.<br>` +
      "Would you like to proceed?";
    connectDialog.dialogType = "WARNING";
    const rslt = await this.confirmDialogService
      .confirmDialog(connectDialog)
      .toPromise();
    return rslt;
  }

  async isSpouseOnTitleChanged(event): Promise<void> {
    const canContinue = await this.confirmSpouseOnTitleChange();

    if (!canContinue) {
      Utils.resetField(
        this.formGroup.controls.isSpouseOnTitle,
        this.oldSpouseOnTitle
      );
      return;
    }

    let currentValue: string = this.formGroup.controls.isSpouseOnTitle.value;

    if (
      currentValue == DpBooleanValueTypes.BLANK ||
      (currentValue == DpBooleanValueTypes.NO &&
        this.formGroup.getRawValue().spouseId)
    ) {
      //remove spouse from clients
      this.deleteSpouse(this.formGroup.getRawValue().spouseId);
      if (currentValue == DpBooleanValueTypes.BLANK) {
        Utils.resetField(this.formGroup.controls.spouseName, "");
      }
      this.formGroup.get("spouseName").enable();
      this.isSpouseNameReadonly = false;
    } else if (
      currentValue == DpBooleanValueTypes.YES &&
      this.formGroup.getRawValue().spouseName
    ) {
      // we need to reset spouse contact, remove and re-create as a new clientInfo
      this.removeClientWithName(this.formGroup.getRawValue().spouseName);
      this.createNewLinkedSpouse(this.formGroup.getRawValue().spouseName);
      this.isSpouseNameReadonly = true;
    }

    if (
      currentValue == DpBooleanValueTypes.YES &&
      !this.formGroup.getRawValue().spouseName
    ) {
      Utils.resetField(this.formGroup.controls.spouseName, "");
      this.isSpouseNameReadonly = false;
    }
  }

  async confirmSpouseOnTitleChange(): Promise<boolean> {
    const newValue: string = this.formGroup.controls.isSpouseOnTitle.value;
    const spouseName = this.formGroup.getRawValue().spouseName;
    if (!spouseName || this.oldSpouseOnTitle === "") {
      return true;
    }
    const connectDialog = new ConnectDialog();
    connectDialog.dialogType = "WARNING";
    connectDialog.okBtnText = "Yes";
    connectDialog.cancelBtnText = "No";
    if (this.oldSpouseOnTitle === "YES" && newValue === "NO") {
      connectDialog.message = `Are you sure you would like to proceed with deleting ${spouseName} as a client to this transaction?`;
    }
    if (this.oldSpouseOnTitle === "NO" && newValue === "YES") {
      connectDialog.message = `Please note that spousal details for ${this.clientName} will be reset.<br>Would you like to proceed?`;
    }

    if (
      (this.oldSpouseOnTitle === "YES" || this.oldSpouseOnTitle === "NO") &&
      newValue === ""
    ) {
      connectDialog.message = `Please note that spousal details for ${this.clientName} will be reset and removed.<br>Would you like to proceed?`;
    }

    if (connectDialog.message) {
      const rslt = await this.confirmDialogService
        .confirmDialog(connectDialog)
        .toPromise();
      return rslt;
    }
    return true;
  }

  isSpouseOnTitle(): boolean {
    return (
      this.formGroup &&
      this.formGroup.getRawValue().isSpouseOnTitle &&
      this.formGroup.getRawValue().isSpouseOnTitle == DpBooleanValueTypes.YES
    );
  }

  isSpouseNotOnTitle(): boolean {
    return (
      this.formGroup?.getRawValue().isSpouseOnTitle == DpBooleanValueTypes.NO
    );
  }

  isSpouseNameVisible(): boolean {
    return (
      this.isSpousalStatusMarried() &&
      (this.isSpouseNotOnTitle() ||
        (this.isSpouseOnTitle() && this.cirf.isParticipantCirf()))
    );
  }

  isSpouseNameAutoSuggestVisible(): boolean {
    return (
      this.isSpouseOnTitle() &&
      this.isSpousalStatusMarried() &&
      this.cirf?.isMatterCirf()
    );
  }

  isSecondaryIdentificationTypeCodeCreditCard(): boolean {
    return (
      this.formGroup &&
      this.formGroup.getRawValue().identifications &&
      this.formGroup.getRawValue().identifications.length > 1 &&
      this.formGroup.getRawValue().identifications[1].identificationTypeCode ==
        CREDIT_CARD
    );
  }

  get cirf(): Cirf {
    return this.cirfService.cirf;
  }

  get permanentResidentCard(): CirfDocument {
    if (this.cirfDocuments && this.cirfDocuments.length) {
      return this.cirfDocuments.find(
        (doc) =>
          doc.sourceId == this.clientId &&
          doc.description == UploadedFileDescription.permanentResidentCard
      );
    } else {
      return null;
    }
  }

  get permanentResidentCardDescription(): string {
    return UploadedFileDescription.permanentResidentCard;
  }

  get driverLicenseFront(): CirfDocument {
    if (this.cirfDocuments && this.cirfDocuments.length) {
      return this.cirfDocuments.find(
        (doc) =>
          doc.sourceId == this.clientId &&
          doc.description == UploadedFileDescription.driverLicenseFront
      );
    } else {
      return null;
    }
  }

  get driverLicenseFrontDescription(): string {
    return UploadedFileDescription.driverLicenseFront;
  }

  get driverLicenseBack(): CirfDocument {
    if (this.cirfDocuments && this.cirfDocuments.length) {
      return this.cirfDocuments.find(
        (doc) =>
          doc.sourceId == this.clientId &&
          doc.description == UploadedFileDescription.driverLicenseBack
      );
    } else {
      return null;
    }
  }

  get driverLicenseBackDescription(): string {
    return UploadedFileDescription.driverLicenseBack;
  }

  deleteDocument(document: CirfDocument) {
    let connectDialog = new ConnectDialog();
    connectDialog.message = "Are you sure you want to delete this document?";
    connectDialog.dialogType = "WARNING";
    this.confirmDialogService.confirmDialog(connectDialog).subscribe((res) => {
      if (res) {
        this.fileUploadService
          .deleteCirfDocument(this.cirf.cirfGuid, String(document.id))
          .subscribe((res) => {
            this.startPolling();
            if (
              document.description == this.driverLicenseBackDescription ||
              document.description == this.driverLicenseFrontDescription
            ) {
              this.handleDeleteDriverLicense();
            }
          });
      }
    });
  }

  async documentUploaded(event) {
    this.startPolling();
  }

  public startPolling(): void {
    this.getCirfDocuments();
    clearInterval(this.scheduler);
    this.scheduler = setInterval(() => {
      this.getCirfDocuments();
    }, 5000);
  }

  stopPolling(): void {
    window.clearInterval(this.scheduler);
  }

  getCirfDocuments(): void {
    this.fileUploadService
      .getCirfDocuments(this.cirf.cirfGuid)
      .subscribe((documents) => {
        this.cirfDocuments = documents;
        this.loadingDocumentsComplete = true;
        if (this.driverLicenseBack && this.driverLicenseFront) {
          let client = this.cirfService.clients.find(
            (client) => client.id == this.clientId
          );
          if (!this._cirfClientInfo.showAllFields) {
            client.showAllFields = true;
            this._cirfClientInfo.showAllFields = true;
            this.saveCirf();
            this.onChanges();
            if (this._cirfClientInfo.isClientInfoFromDL) {
              this.disableFormControls();
            }
          }

          this.updateValidation();
        }
        if (this.cirfDocuments && !this.cirfDocuments.length) {
          this.stopPolling();
        } else {
          if (this.cirfDocuments.every((doc) => doc.isVirusClean())) {
            this.stopPolling();
          }
        }
      });
  }

  get clientName(): string {
    //Using getRawValue() instead of value to get the values if the field is disabled
    return (
      (this.formGroup?.getRawValue()?.firstName
        ? this.formGroup.getRawValue().firstName + " "
        : "") +
      (this.formGroup?.getRawValue()?.middleName
        ? this.formGroup.getRawValue().middleName + " "
        : "") +
      (this.formGroup?.getRawValue()?.lastName
        ? this.formGroup.getRawValue().lastName
        : "")
    );
  }

  ngOnDestroy(): void {
    this.saveClientNotes();
    this.cirfService.updateCirf("INPROGRESS");
    this.stopPolling();
    if (this.spousalStatusUpdateSubscription) {
      this.spousalStatusUpdateSubscription.unsubscribe();
    }
    if (this.spouseOnTitleSubscription) {
      this.spouseOnTitleSubscription.unsubscribe();
    }
  }

  isUploadDriverLicenseYes(): boolean {
    return this._cirfClientInfo.agreedToUploadDL == DpBooleanValueTypes.YES;
  }

  isUploadDriverLicenseNo(): boolean {
    return this._cirfClientInfo.agreedToUploadDL == DpBooleanValueTypes.NO;
  }

  isUploadDriverLicenseEmpty(): boolean {
    return !this._cirfClientInfo.agreedToUploadDL;
  }

  showOrHideAllFields() {
    let cirfClientInfo = this.cirfService.clients.find(
      (item) => item.id == this.clientId.toString()
    );
    cirfClientInfo.agreedToUploadDL = this._cirfClientInfo.agreedToUploadDL;
    this._cirfClientInfo = cirfClientInfo;
    if (this.isUploadDriverLicenseNo()) {
      if (!this._cirfClientInfo.showAllFields) {
        this._cirfClientInfo.showAllFields = true;
      }

      this.updateValidation();
    }
    this.saveCirf();
    this.onChanges();
  }

  async driverLicenseUploaded(event) {
    if (event.cirfDocument) {
      if (event.cirfDocument.description == this.driverLicenseBackDescription) {
        if (event.driverLicenseInfo) {
          console.log(event.driverLicenseInfo);
          this.loadDriverLicenseData(event.driverLicenseInfo);
        } else {
          let cirfClientInfo = this.cirfService.clients.find(
            (item) => item.id == this.clientId.toString()
          );
          this.updateIdentificationFromDriverLicense(null, cirfClientInfo);
          this._cirfClientInfo = cirfClientInfo;
        }
      }
      this.startPolling();
    }
  }

  loadDriverLicenseData(driverLicenseInfo: DriverLicenseInfo) {
    let cirfClientInfo = this.cirfService.clients.find(
      (item) => item.id == this.clientId.toString()
    );
    cirfClientInfo.isClientInfoFromDL = true;
    cirfClientInfo.firstName = driverLicenseInfo.getFirstName();
    cirfClientInfo.middleName = driverLicenseInfo.getMiddleName();
    cirfClientInfo.lastName = driverLicenseInfo.surName;
    cirfClientInfo.gender = driverLicenseInfo.gender;
    cirfClientInfo.birthDate = moment
      .utc(driverLicenseInfo.birthDate)
      .format("YYYY/MM/DD");
    let businessAddress = cirfClientInfo.businessAddress;
    businessAddress.addressLine1 = Utils.toMixedCase(
      driverLicenseInfo.streetAddress
    );
    businessAddress.city = Utils.toMixedCase(driverLicenseInfo.city);
    businessAddress.provinceCode = driverLicenseInfo.province;
    businessAddress.postalCode = driverLicenseInfo.postalCode;
    businessAddress.country = "CANADA";
    this.updateIdentificationFromDriverLicense(
      driverLicenseInfo,
      cirfClientInfo
    );
    this._cirfClientInfo = cirfClientInfo;
    this.saveCirf();
    this.disableFormControls();
  }

  updateIdentificationFromDriverLicense(
    driverLicenseInfo: DriverLicenseInfo,
    cirfClientInfo: CirfClientInfo
  ) {
    let identification: Identification;
    if (cirfClientInfo.identifications.length) {
      identification = cirfClientInfo.identifications[0];
    } else {
      identification = new Identification();
      cirfClientInfo.identifications.push(identification);
    }
    let driverLicenseTypeCode = this.filteredPrimaryIdentificationOptions.find(
      (id) => id.name.startsWith("Driver")
    );
    identification.identificationTypeCode = driverLicenseTypeCode.name;
    identification.identificationNumber =
      driverLicenseInfo && driverLicenseInfo.licenseNumber;
    identification.placeOfIssue =
      driverLicenseInfo && driverLicenseInfo.placeOfIssue;
    identification.expiryDate =
      driverLicenseInfo &&
      driverLicenseInfo.formatDate(driverLicenseInfo.expiryDate, true);
    identification.country = "Canada";
  }

  disableFormControls() {
    let cirfClientInfo = this.cirfService.clients.find(
      (item) => item.id == this.clientId.toString()
    );
    if (cirfClientInfo.firstName) {
      this.formGroup.get("firstName").disable();
    }
    if (cirfClientInfo.middleName) {
      this.formGroup.get("middleName").disable();
    }
    if (cirfClientInfo.lastName) {
      this.formGroup.get("lastName").disable();
    }
    if (cirfClientInfo.gender) {
      this.formGroup.get("gender").disable();
    }
    if (cirfClientInfo.birthDate) {
      this.formGroup.get("birthDate").disable();
    }

    this.formGroup.get("addresses").get("0").disable();
    this.formGroup.get("identifications").get("0").disable();
  }

  enableFormControls() {
    this.formGroup.get("firstName").enable();
    this.formGroup.get("middleName").enable();
    this.formGroup.get("lastName").enable();
    this.formGroup.get("gender").enable();
    this.formGroup.get("birthDate").enable();
    this.formGroup.get("addresses").get("0").enable();
    this.formGroup.get("identifications").get("0").enable();
    this.enableMaterialControls();
  }

  /*
   * Material 'Select' and 'Date Picker' fields don't realy work properly when enabled after being disabled
   * enableMaterialControls() handle those fields explicitly.
   */
  enableMaterialControls() {
    this.genderSelect.disabled = false;
    this.birthDatePicker.disabled = false;
    this.primaryExpiryDatePicker.disabled = false;
    this.businessAddress.provinceSelect.disabled = false;
    this.businessAddress.countrySelect.disabled = false;
  }

  handleDeleteDriverLicense() {
    let cirfClientInfo = this.cirfService.clients.find(
      (item) => item.id == this.clientId.toString()
    );
    if (cirfClientInfo) {
      cirfClientInfo.isClientInfoFromDL = false;
      cirfClientInfo.agreedToUploadDL = DpBooleanValueTypes.NO;
      this.saveCirf();
      this.enableFormControls();
      this.onChanges();
    }
  }

  spouseSelected(event: any) {
    if (event && event.value) {
      //spouse selected from dropdown options
      let spouseId: string = event.value;
      this.isSpouseNameReadonly = true;

      //selected existing spouse, no action required
      if (spouseId == this.formGroup.getRawValue().spouseId) {
        return;
      }

      //remove any links from existing spouse (if any)
      this.removeSpousalLink(this.formGroup.getRawValue().spouseId);

      this.formGroup.get("spouseId").setValue(spouseId);
      this.formGroup.get("spouseName").setValue(event.displayName);
      this.formGroup.get("spouseName").disable();

      let spouseFormGroup: FormGroup =
        this.getCirfClientFormGroupById(spouseId);

      //spouse we are linking to already has a spousal link that needs to be severed
      this.removeSpousalLink(spouseFormGroup.getRawValue().spouseId);

      spouseFormGroup.get("spouseId").setValue(this.clientId);
      spouseFormGroup.get("spouseName").setValue(this.clientName);
      spouseFormGroup.get("spousalStatus").setValue(this.getSpousalStatus());
      spouseFormGroup
        .get("isFamilyResidence")
        .setValue(this.getFamilyResidence());
      spouseFormGroup.get("isSpouseOnTitle").setValue(this.getSpouseOnTitle());
    } else if (event.displayName) {
      //user typed spouse name manually
      this.createNewLinkedSpouse(event.displayName);
      this.formGroup.get("spouseName").disable();
      this.isSpouseNameReadonly = true;
      this.cirfService.buildNavigationSections();
    }
  }

  createNewLinkedSpouse(spouseName: string) {
    if (this.cirf.isParticipantCirf()) {
      return;
    }

    //remove any links from existing spouse (if any)
    this.removeSpousalLink(this.formGroup.getRawValue().spouseId);

    let newClient: CirfClientInfo = new CirfClientInfo();
    newClient.id = Utils.uuid();
    this.formGroup.get("spouseId").setValue(newClient.id); //link new spouse to this client
    this.formGroup.get("spouseName").setValue(spouseName);

    let names: string[] = Utils.parseLastFirstMiddleNames(spouseName);
    if (names.length) {
      newClient.lastName = names[0];
      if (names.length > 1) {
        newClient.firstName = names[1];
      }
      if (names.length > 2) {
        newClient.middleName = names[2];
      }
    }

    if (this.formGroup.getRawValue().gender === "MALE") {
      newClient.gender = "FEMALE";
    } else if (this.formGroup.getRawValue().gender === "FEMALE") {
      newClient.gender = "MALE";
    }

    newClient.spousalStatus = "MARRIED";
    newClient.isFamilyResidence = this.getFamilyResidence();
    newClient.isSpouseOnTitle = "YES";
    newClient.spouseId = this.clientId;
    newClient.spouseName = this.clientName;

    this.cirfClientsFormArray.push(
      this.commonService.createFormControls(newClient)
    );
    if (this.cirfService.clients && this.cirfService.clients.length) {
      this.cirfService.clients.push(newClient);
    }
    this.loadSpouseDropDownOptions();
  }

  removeSpousalLink(spouseId: string) {
    if (spouseId) {
      const spouseFormGroup: FormGroup =
        this.getCirfClientFormGroupById(spouseId);
      if (spouseFormGroup) {
        spouseFormGroup.get("spouseId").setValue("");
        spouseFormGroup.get("spouseName").setValue("");
      }
    }
  }

  removeSpouse(): void {
    const connectDialog = new ConnectDialog();
    const spouseName = this.formGroup.getRawValue().spouseName;
    connectDialog.message = `Are you sure you would like to remove ${spouseName} as ${this.clientName}'s spouse? <br>Please note ${spouseName} will still remain as a contact on the file.`;
    connectDialog.dialogType = "WARNING";
    connectDialog.cancelBtnText = "Cancel";
    connectDialog.okBtnText = "Remove";
    this.confirmDialogService.confirmDialog(connectDialog).subscribe((res) => {
      if (res) {
        this.removeSpousalLink(this.formGroup.getRawValue().spouseId);
        Utils.resetField(this.formGroup.controls.spouseName, "");
        Utils.resetField(this.formGroup.controls.spouseId, "");
        this.isSpouseNameReadonly = false;
        this.spouseAutoSuggest.clearValue();
      }
    });
  }

  deleteSpouse(spouseId: string): void {
    if (spouseId) {
      let spouseIndex: number = this.cirfClientsFormArray.value.findIndex(
        (client) => client.id == spouseId
      );
      if (spouseIndex > -1) {
        this.cirfClientsFormArray.removeAt(spouseIndex);
        this.loadSpouseDropDownOptions();
      }
      this.formGroup.get("spouseId").setValue("");
    }
  }

  get emailPattern() {
    return Utils.EMAIL_ADDRESS_PATTERN;
  }

  get allowMissingFields(): boolean {
    return this._cirfClientInfo?.allowMissingFields;
  }

  changeAllowMissingFields(allowMissingFields: boolean) {
    let cirfClientInfo = this.cirfService.clients.find(
      (item) => item.id == this.clientId.toString()
    );
    cirfClientInfo.allowMissingFields = allowMissingFields;
    this._cirfClientInfo = cirfClientInfo;
    this.saveCirf();
    this.onChanges();
  }

  updateRequiredAttributes(): void {
    Utils.updateFormGroupRequiredAttributesForClient(
      this.formGroup,
      !this.allowMissingFields,
      this.emailPattern
    );
    Utils.invokeFieldLevelValidation(this.formGroup);
  }

  updateValidation() {
    setTimeout(() => {
      this.updatePhoneValidation();
      this.updateRequiredAttributes();
    }, 0);
  }

  //In case of invalid date, set the value to null
  validateDate(
    event: MatDatepickerInputEvent<Date>,
    formControlName: string,
    formArrayName?: string,
    arrayIndex?: string
  ) {
    setTimeout(() => {
      //event.value is null in case of invalid date or the actual value is null
      if (!event.value) {
        if (formArrayName) {
          this.formGroup
            .get(formArrayName)
            .get(arrayIndex)
            .get(formControlName)
            .setValue(null);
        } else {
          this.formGroup.get(formControlName).setValue(null);
        }
      }
    }, 1000);
  }

  validateExpiryDate(
    formControlName: string,
    formArrayName?: string,
    arrayIndex?: string
  ): void {
    setTimeout(() => {
      if (this.checkError(formControlName, formArrayName, arrayIndex)) {
        this.formGroup
          .get(formArrayName)
          .get(arrayIndex)
          .get(formControlName)
          .setValue(null);
      }
    }, 1000);
  }

  setExpiryDate(
    formControlName: string,
    formArrayName?: string,
    arrayIndex?: string,
    event?: MatDatepickerInputEvent<Date>
  ) {
    let date = moment(event.value).format("DD/MM/YYYY");
    this.formGroup
      .get(formArrayName)
      .get(arrayIndex)
      .get(formControlName)
      .setValue(date);
  }

  checkError(
    formControlName: string,
    formArrayName?: string,
    arrayIndex?: string
  ): boolean {
    return this.formGroup
      .get(formArrayName)
      .get(arrayIndex)
      .get(formControlName)
      .hasError("invalidDate");
  }

  checkWarning(
    formControlName: string,
    formArrayName?: string,
    arrayIndex?: string
  ): boolean {
    let isValidDate = !this.checkError(
      formControlName,
      formArrayName,
      arrayIndex
    );
    let isValidPartialDate = Utils.checkValidPartialDate(
      this.formGroup?.getRawValue()?.identifications[arrayIndex]?.expiryDate
    );
    return isValidDate && isValidPartialDate;
  }

  checkValidPartialDate(): void {}

  getUploadedDocument(index: number, direction: string): CirfDocument {
    let identificationTypeCode =
      this.formGroup?.getRawValue()?.identifications[index]
        ?.identificationTypeCode;
    if (
      this.cirfDocuments &&
      this.cirfDocuments.length &&
      identificationTypeCode
    ) {
      let fileDescrition =
        direction == this.frontText
          ? this.getUploadedFileFrontDescription(identificationTypeCode)
          : this.getUploadedFileBackDescription(identificationTypeCode);
      return this.cirfDocuments.find(
        (doc) =>
          doc.sourceId == this.clientId && doc.description == fileDescrition
      );
    } else {
      return null;
    }
  }

  isIdentificationSelected(index: number): boolean {
    return (
      this.formGroup?.getRawValue()?.identifications?.length > 0 &&
      this.formGroup?.getRawValue()?.identifications[index]
        ?.identificationTypeCode
    );
  }

  //Upload Buttons are visible when there identification is selected and no corresponding document found
  isUploadIdVisible(index: number, direction: string): boolean {
    let identificationTypeCode =
      this.formGroup?.getRawValue()?.identifications[index]
        ?.identificationTypeCode;
    if (
      identificationTypeCode == PASSPORT_CANADIAN_ID &&
      direction == this.backText
    ) {
      return false;
    } else if (this.isIdentificationSelected(index) && identificationTypeCode) {
      let fileDescrition =
        direction == this.frontText
          ? this.getUploadedFileFrontDescription(identificationTypeCode)
          : this.getUploadedFileBackDescription(identificationTypeCode);
      return !this.cirfDocuments.find(
        (doc) =>
          doc.sourceId == this.clientId && doc.description == fileDescrition
      );
    } else {
      return false;
    }
  }

  getUploadText(index: number): string {
    let identificationTypeCode =
      this.formGroup?.getRawValue()?.identifications[index]
        ?.identificationTypeCode;

    if (identificationTypeCode == PASSPORT_CANADIAN_ID) {
      return "Please Upload the personal data page of the passport, including both the signature and photograph sections.";
    } else {
      return `Please Upload the Front and Back of the ${
        this.formGroup?.getRawValue()?.identifications[index]
          ?.identificationTypeCode
      }`;
    }
  }

  getUploadButtonText(index: number, direction: string): string {
    let identificationTypeCode =
      this.formGroup?.getRawValue()?.identifications[index]
        ?.identificationTypeCode;
    if (identificationTypeCode == PASSPORT_CANADIAN_ID) {
      return UploadedFileDescription.passportCanadianId;
    } else {
      return `${direction} of the ${identificationTypeCode}`;
    }
  }

  getUploadedFileFrontDescription(identificationTypeCode: string): string {
    return Utils.getUploadedFileFrontDescription(identificationTypeCode);
  }

  getUploadedFileBackDescription(identificationTypeCode: string): string {
    return Utils.getUploadedFileBackDescription(identificationTypeCode);
  }

  getUploadedFileDescription(index: number, direction: string): string {
    let identificationTypeCode =
      this.formGroup?.getRawValue()?.identifications[index]
        ?.identificationTypeCode;
    if (identificationTypeCode) {
      return direction == this.frontText
        ? this.getUploadedFileFrontDescription(identificationTypeCode)
        : this.getUploadedFileBackDescription(identificationTypeCode);
    }
    return "";
  }

  saveCirf() {
    this.cirfService.setUpFormControl(this.cirf);
    this.setupClientNotes();
    this.saveClientForm();
  }

  anyIdentificationFieldVisible(): boolean {
    return !(
      this.cirfConfigService.areFieldsHidden(
        "cirfClientInfo.identification1.identificationTypeCode;cirfClientInfo.identification1.identificationNumber;cirfClientInfo.identification1.placeOfIssue;cirfClientInfo.identification1.expiryDate;cirfClientInfo.identification1.country;cirfClientInfo.identification2.identificationTypeCode;cirfClientInfo.identification2.identificationNumber;cirfClientInfo.identification2.placeOfIssue;cirfClientInfo.identification2.expiryDate;cirfClientInfo.identification2.country"
      ) && this.areIdentificationUDFsHidden()
    );
  }

  areIdentificationUDFsHidden(): boolean {
    return (
      this.cirfConfigService.areAllUDFsHidden(
        CIRF_CONFIG_KEYS.identification1Udf,
        this.primaryIdentificationUdfs && this.primaryIdentificationUdfs.length
      ) &&
      this.cirfConfigService.areAllUDFsHidden(
        CIRF_CONFIG_KEYS.identification2Udf,
        this.secondaryIdentificationUdfs &&
          this.secondaryIdentificationUdfs.length
      )
    );
  }

  isClientNotesVisible(): boolean {
    return !this.cirfConfigService.isFieldHidden("cirfClientInfo.clientNotes");
  }

  get cirfClientInfoUdfs(): CirfUdf[] {
    return this._cirfClientInfo?.cirfClientInfoUdfs;
  }

  get cirfClientInfoUdfFieldKey(): string {
    return CIRF_CONFIG_KEYS.cirfClientInfoUdf;
  }

  get primaryIdentificationUdfs(): CirfUdf[] {
    return this._cirfClientInfo?.identifications[0]?.identificationUdfs;
  }

  get primaryIdentificationUdfFieldKey(): string {
    return CIRF_CONFIG_KEYS.identification1Udf;
  }

  get secondaryIdentificationUdfs(): CirfUdf[] {
    return this._cirfClientInfo?.identifications[1]?.identificationUdfs;
  }

  get secondaryIdentificationUdfFieldKey(): string {
    return CIRF_CONFIG_KEYS.identification2Udf;
  }

  get primaryIdentificationFormGroup(): FormGroup {
    return this.formGroup["controls"].identifications["controls"][0];
  }

  get secondaryIdentificationFormGroup(): FormGroup {
    return this.formGroup["controls"].identifications["controls"][1];
  }

  isSelectedSectionPersonalDetails(): boolean {
    return this.sectionKey == CIRF_NAME_KEYS.personalDetails.key;
  }

  isSelectedSectionContactDetails(): boolean {
    return this.sectionKey == CIRF_NAME_KEYS.contactDetails.key;
  }

  isSelectedSectionIdVerification1(): boolean {
    return this.sectionKey == CIRF_NAME_KEYS.idVerification1.key;
  }

  isSelectedSectionIdVerification2(): boolean {
    return this.sectionKey == CIRF_NAME_KEYS.idVerification2.key;
  }

  isSelectedSectionNotes(): boolean {
    return this.sectionKey == CIRF_NAME_KEYS.clientNotes.key;
  }

  toggleUploadingTips(): void {
    let width = "0px";
    let height = "0px";
    let elementWidth = document
      .getElementById("uploadingTipsDiv")
      ?.style.getPropertyValue("width");
    if (elementWidth == "" || elementWidth == "0px") {
      width = "250px";
      height = "400px";
    }
    document.getElementById("uploadingTipsDiv").style.width = width;
    document.getElementById("uploadingTipsDiv").style.height = height;
  }

  get showAllFields(): boolean {
    return this._cirfClientInfo?.showAllFields;
  }
}
